import { isValid, parseISO } from 'date-fns';

import { UpdateDeveloperDto, ValidationError } from '@internal/types/api';
import { Developer } from '@internal/types/developers';

import { FormDeveloper } from './types';

const urlRegex = /^(https?:\/\/|w{3}\.)[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*)$/;

export const developersToFormDevelopers = (
  developers: Array<Developer>
): Array<FormDeveloper> =>
  developers.map<FormDeveloper>(developer => ({
    id: developer.id,
    originalId: developer.id,
    apiKey: developer.apiKey,
    lemonId: developer.lemonId,
    fullName: `${developer.firstName} ${developer.lastName}`,
    email: developer.email,
    seniority: developer.specializations[0]?.seniority ?? null,
    specialization: developer.specializations[0],
    coreStack: developer.mainSkills.map(skill =>
      typeof skill === 'string' ? skill : skill.name
    ),
    rate:
      typeof developer.max_rate_per_hour === 'number'
        ? String(developer.max_rate_per_hour)
        : developer.max_rate_per_hour,
    availability: developer.available_hours_per_week,
    experience: isValid(parseISO(developer.experience_since))
      ? parseISO(developer.experience_since).getFullYear()
      : Number(
          Number.isNaN(Number(developer.experience_since))
            ? 0
            : developer.experience_since
        ),
    englishLevel: developer.en_spoken || null,
    cvLink: developer.link_to_cv,
    cvFile: developer.cv_filename
      ? { name: developer.cv_filename, url: developer.cv_url }
      : null,
    country: developer.country,
    activeSubs: developer.numberOfAppLemonSubscriptions,
    addedProjects: developer.numberOfProjects,
    passedEnglishCheck: developer.passedEnglishCheck,
    createdDate: developer.created_date,
    lastChangeDate: developer.last_change_date,
    status: developer.status_in_system,
  }));

export const mapDeveloperFieldsToFormDeveloper = (
  developer: Partial<Developer>
): Partial<FormDeveloper> =>
  Object.fromEntries(
    Object.entries({
      id: developer.id,
      originalId: developer.id,
      apiKey: developer.apiKey,
      fullName: `${developer.firstName || ''} ${developer.lastName || ''}`,
      email: developer.email,
      seniority: developer.specializations?.[0].seniority ?? null,
      specialization: developer.specializations?.[0],
      coreStack:
        developer.mainSkills?.map(skill =>
          typeof skill === 'string' ? skill : skill.name
        ) ?? [],
      rate:
        typeof developer.max_rate_per_hour === 'number'
          ? String(developer.max_rate_per_hour)
          : developer.max_rate_per_hour,
      availability: developer.available_hours_per_week,
      experience:
        typeof developer.experience_since === 'string'
          ? Number(developer.experience_since)
          : developer.experience_since,
      englishLevel: developer.en_spoken,
      cvLink: developer.link_to_cv,
      cvFile: developer.cv_filename
        ? { name: developer.cv_filename, url: developer.cv_url }
        : null,
      country: developer.country,
      activeSubs: developer.numberOfAppLemonSubscriptions,
      addedProjects: developer.numberOfProjects,
      passedEnglishCheck: developer.passedEnglishCheck,
      createdDate: developer.created_date,
      lastChangeDate: developer.last_change_date,
    }).filter(([, v]) => v !== undefined)
  );

export const mapErrorPropertyToFieldName = (
  property: string
): keyof FormDeveloper =>
  (({
    link_to_cv: 'cvLink',
    firstName: 'fullName',
    lastName: 'fullName',
    main_specialization: 'specialization',
    en_spoken: 'englishLevel',
    experience_since: 'experience',
    max_rate_per_hour: 'rate',
    available_hours_per_week: 'availability',
  }[property] as keyof FormDeveloper) || property);

export const isUrl = (str: string) => urlRegex.test(str);

export const adjustUrl = (url: string) =>
  /^(https?:\/\/)/.test(url) ? url : `https://${url}`;

export const isEmail = (str: string): boolean =>
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(
    str
  );

const fieldsOrder: Array<keyof FormDeveloper> = [
  'fullName',
  'email',
  'availability',
  'seniority',
  'specialization',
  'coreStack',
  'rate',
  'experience',
  'englishLevel',
  'cvLink',
  'country',
];

export const getNextFieldName = (
  currentField: keyof FormDeveloper,
  shift = 0
) => {
  const currentIndex = fieldsOrder.indexOf(currentField);
  if (currentIndex !== fieldsOrder.length - 1 - shift) {
    return fieldsOrder[currentIndex + 1 + shift];
  }

  return null;
};

export const createUploadFileError = (
  developer: FormDeveloper,
  message?: string
): {
  updateErrors: Record<string, Array<ValidationError>>;
  creationErrors: Array<ValidationError>;
} => {
  if (developer.id) {
    return {
      creationErrors: [],
      updateErrors: {
        [developer.id]: [
          {
            constraints: {
              error:
                message || 'File is too large for the destination file system',
            },
            property: 'cvFile',
            target: developer as unknown as UpdateDeveloperDto,
            value: developer.cvFile?.name || '',
          },
        ],
      },
    };
  }

  return {
    updateErrors: {},
    creationErrors: [
      {
        constraints: {
          error: 'File is too large for the destination file system',
        },
        property: 'cvFile',
        target: developer as unknown as UpdateDeveloperDto,
        value: developer.cvFile?.name || '',
      },
    ],
  };
};

export enum ObservableInternalKey {
  INITIALIZATION = 'initialization',
  DEVELOPERS = 'developers',
  TOOLTIP_COLUMN_STATE = 'tooltip-column-state',
  DEVELOPER = 'developer',
  DEVELOPER_META = 'developer-meta',
  FIELD = 'field',
  FIELD_META = 'field-meta',
  DEVELOPER_SELECTED = 'developer-selected',
  DEVELOPER_CHANGED = 'developer-changed',
  CELL_CHANGED = 'cell-changed',
  EXACT_CELL_CHANGED = 'exact-cell-changed',
  SORTING = 'sorting',
  COLUMN_HEADER = 'column-header',
  SERVER_VALIDATED = 'server-validated',
}

const OBSERVABLE_KEYS = {
  [ObservableInternalKey.INITIALIZATION]: () => 'initialization' as const,
  [ObservableInternalKey.DEVELOPERS]: () => 'developers' as const,
  [ObservableInternalKey.TOOLTIP_COLUMN_STATE]: () =>
    'tooltip_column_state' as const,
  [ObservableInternalKey.DEVELOPER]: <T extends string>(id: T) =>
    `developer_${id}` as `developer_${T}`,
  [ObservableInternalKey.DEVELOPER_META]: <T extends string>(id: T) =>
    `developer_${id}_meta` as `developer_${T}_meta`,
  [ObservableInternalKey.FIELD]: <T extends string, U extends string>(
    id: T,
    field: U
  ) => `developer_${id}_${field}` as `developer_${T}_${U}`,
  [ObservableInternalKey.FIELD_META]: <T extends string, U extends string>(
    id: T,
    field: U
  ) => `developer_${id}_${field}_meta` as `developer_${T}_${U}_meta`,
  [ObservableInternalKey.DEVELOPER_SELECTED]: <T extends string>(id: T) =>
    `developer_${id}_selected` as `developer_${T}_selected`,
  [ObservableInternalKey.DEVELOPER_CHANGED]: <T extends string>(id: T) =>
    `developer_${id}_changed` as `developer_${T}_changed`,
  [ObservableInternalKey.CELL_CHANGED]: () => 'developer_cell_changed' as const,
  [ObservableInternalKey.EXACT_CELL_CHANGED]: <T extends string>(id: T) =>
    `developer_${id}_cell_changed` as `developer_${T}_cell_changed`,
  [ObservableInternalKey.SORTING]: () => 'sorting' as const,
  [ObservableInternalKey.COLUMN_HEADER]: <T extends string>(field: T) =>
    `column_header_${field}` as `column_header_${T}`,
  [ObservableInternalKey.SERVER_VALIDATED]: () => 'server_validated' as const,
} as const;

export type ObservableKeys = ReturnType<
  typeof OBSERVABLE_KEYS[keyof typeof OBSERVABLE_KEYS]
>;

export type ResolveArgs<K extends ObservableInternalKey> = Parameters<
  typeof OBSERVABLE_KEYS[K]
>;

export const getObservableKey = <K extends ObservableInternalKey>(key: K) => ({
  resolve: OBSERVABLE_KEYS[key] as (...args: ResolveArgs<K>) => ObservableKeys,
});
